import { useEffect, useRef } from "react";
import { Grid, Card, CardContent, Typography, Box } from "@material-ui/core";
import { Chart } from "chart.js";
import BigNumberCard from "components/_refactor/card/big-number";
import MaskAndValid from "utils/maskAndValid";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LoadingData from "components/Loading/LoadingData";

const COLORS = ["#156082", "#4CAF50", "#994E53", "#fb6340", "#FEA9B0"];

const parsePriceString = (priceString) => {
  if (!priceString) return 0;
  if (typeof priceString === "number") return priceString;
  if (typeof priceString !== "string") return 0;

  const digitsOnly = MaskAndValid.maskNumber(priceString);
  if (!digitsOnly) return 0;

  const hasDecimal = priceString.includes(",");
  if (hasDecimal) {
    return parseInt(digitsOnly, 10) / 100;
  }

  return parseInt(digitsOnly, 10);
};

export default function Faturamento({ data, loading }) {
  const marcaChartRef = useRef(null);
  const marcaChartInstance = useRef(null);
  const linhaProdutoChartRef = useRef(null);
  const linhaProdutoChartInstance = useRef(null);
  const produtosChartRefs = useRef({});
  const produtosChartInstances = useRef({});

  useEffect(() => {
    if (data) {
      requestAnimationFrame(() => {
        if (marcaChartRef.current) {
          renderMarcaChart();
        }
        if (linhaProdutoChartRef.current) {
          renderLinhaProdutoChart();
        }
        if (data?.RankingProdutos && data?.RankingProdutos?.length > 0) {
          renderProdutosCharts();
        }
      });
    }

    return () => {
      if (marcaChartInstance.current) {
        marcaChartInstance.current.destroy();
        marcaChartInstance.current = null;
      }
      if (linhaProdutoChartInstance.current) {
        linhaProdutoChartInstance.current.destroy();
        linhaProdutoChartInstance.current = null;
      }
      Object.values(produtosChartInstances.current).forEach((instance) => {
        if (instance) {
          instance.destroy();
        }
      });
      produtosChartInstances.current = {};
      produtosChartRefs.current = {};
    };
  }, [data]);

  const renderMarcaChart = () => {
    if (!data?.ResumoMarca || marcaChartRef.current === null) return;

    if (marcaChartInstance.current) {
      marcaChartInstance.current.destroy();
    }

    const ctx = marcaChartRef.current.getContext("2d");

    const labels = data.ResumoMarca.map((m) => m.Marca);
    const totalData = data.ResumoMarca.map((m) =>
      parsePriceString(m.ValorPedidos)
    );
    const faturadoData = data.ResumoMarca.map((m) =>
      parsePriceString(m.ValorFaturado)
    );
    const aFaturarData = data.ResumoMarca.map((m) =>
      parsePriceString(m.ValorEmAberto)
    );

    const chartData = {
      labels: labels,
      datasets: [
        {
          label: "Total de Pedidos",
          data: totalData,
          backgroundColor: "#156082",
          borderColor: "#156082",
          borderWidth: 0,
        },
        {
          label: "Faturado",
          data: faturadoData,
          backgroundColor: "#4CAF50",
          borderColor: "#4CAF50",
          borderWidth: 0,
        },
        {
          label: "Em Aberto",
          data: aFaturarData,
          backgroundColor: "#9E9E9E",
          borderColor: "#9E9E9E",
          borderWidth: 0,
        },
      ],
    };

    const options = {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "top",
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              return `${context.dataset.label}: R$ ${MaskAndValid.maskPrice(
                context.raw
              )}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          ticks: {
            callback: function (value) {
              return "R$ " + MaskAndValid.maskPrice(value);
            },
          },
          grid: {
            color: "#e5e5e5",
          },
        },
        y: {
          stacked: true,
          grid: {
            display: false,
          },
        },
      },
    };

    marcaChartInstance.current = new Chart(ctx, {
      type: "bar",
      data: chartData,
      options: options,
    });
  };

  const renderLinhaProdutoChart = () => {
    if (!data?.ResumoLinhaProduto || linhaProdutoChartRef.current === null)
      return;

    if (linhaProdutoChartInstance.current) {
      linhaProdutoChartInstance.current.destroy();
    }

    const ctx = linhaProdutoChartRef.current.getContext("2d");

    const labels = data.ResumoLinhaProduto.map((l) => l.LinhaProduto);
    const totalData = data.ResumoLinhaProduto.map((l) =>
      parsePriceString(l.ValorPedidos)
    );
    const faturadoData = data.ResumoLinhaProduto.map((l) =>
      parsePriceString(l.ValorFaturado)
    );
    const aFaturarData = data.ResumoLinhaProduto.map((l) =>
      parsePriceString(l.ValorEmAberto)
    );

    const chartData = {
      labels: labels,
      datasets: [
        {
          label: "Total de Pedidos",
          data: totalData,
          backgroundColor: "#156082",
          borderColor: "#156082",
          borderWidth: 0,
        },
        {
          label: "Faturado",
          data: faturadoData,
          backgroundColor: "#4CAF50",
          borderColor: "#4CAF50",
          borderWidth: 0,
        },
        {
          label: "Em Aberto",
          data: aFaturarData,
          backgroundColor: "#9E9E9E",
          borderColor: "#9E9E9E",
          borderWidth: 0,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "top",
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              return `${context.dataset.label}: R$ ${MaskAndValid.maskPrice(
                context.raw
              )}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            color: "#e5e5e5",
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: {
            callback: function (value) {
              return "R$ " + MaskAndValid.maskPrice(value);
            },
          },
          grid: {
            color: "#e5e5e5",
          },
        },
      },
    };

    linhaProdutoChartInstance.current = new Chart(ctx, {
      type: "bar",
      data: chartData,
      options: options,
    });
  };

  const renderProdutosCharts = () => {
    if (!data?.RankingProdutos) return;

    const linhasAgrupadas = data.RankingProdutos.reduce((acc, produto) => {
      const linha = produto.LinhaProduto || "Outros";
      if (!acc[linha]) {
        acc[linha] = [];
      }
      acc[linha].push(produto);
      return acc;
    }, {});

    Object.entries(linhasAgrupadas).forEach(([linha, produtos], linhaIndex) => {
      const chartKey = linha;
      const chartRef = produtosChartRefs.current[chartKey];
      const chartInstance = produtosChartInstances.current[chartKey];

      if (!chartRef || chartRef === null) return;

      if (chartInstance) {
        chartInstance.destroy();
      }

      const ctx = chartRef.getContext("2d");

      const sortedProdutos = produtos.sort((a, b) => {
        const qtyA = parseFloat(a.Quantidade) || 0;
        const qtyB = parseFloat(b.Quantidade) || 0;
        return qtyB - qtyA;
      });

      const labels = sortedProdutos.map((p) => p.DescricaoProduto || "");
      const quantities = sortedProdutos.map(
        (p) => parseFloat(p.Quantidade) || 0
      );

      const chartData = {
        labels: labels,
        datasets: [
          {
            label: "Quantidade",
            data: quantities,
            backgroundColor: COLORS[linhaIndex % COLORS.length],
            borderColor: COLORS[linhaIndex % COLORS.length],
            borderWidth: 0,
            barThickness: "flex",
            maxBarThickness: 24,
            categoryPercentage: 0.6,
            barPercentage: 1.0,
          },
        ],
      };

      const options = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "top",
          align: "center",
          labels: {
            boxWidth: 12,
            padding: 4,
            fontFamily: "Open Sans",
            fontSize: 10,
            generateLabels: function () {
              return [
                {
                  text: "Quantidade",
                  fillStyle: COLORS[linhaIndex % COLORS.length],
                  strokeStyle: COLORS[linhaIndex % COLORS.length],
                  lineWidth: 0,
                  hidden: false,
                  index: 0,
                },
              ];
            },
          },
        },
        tooltips: {
          callbacks: {
            title: function (tooltipItems) {
              const fullLabel =
                labels[tooltipItems[0].index] ||
                tooltipItems[0].yLabel ||
                "";
              if (fullLabel.length <= 40) return fullLabel;
              const words = fullLabel.split(" ");
              const lines = [];
              let currentLine = "";
              words.forEach((word) => {
                if ((currentLine + " " + word).trim().length > 40 && currentLine) {
                  lines.push(currentLine.trim());
                  currentLine = word;
                } else {
                  currentLine = currentLine ? currentLine + " " + word : word;
                }
              });
              if (currentLine) lines.push(currentLine.trim());
              return lines;
            },
            label: function (tooltipItem) {
              return `${tooltipItem.xLabel} unidades`;
            },
          },
          backgroundColor: "#1e293b",
          titleFontColor: "#ffffff",
          bodyFontColor: "#ffffff",
          borderColor: "#1e293b",
          borderWidth: 1,
          padding: 8,
          displayColors: false,
        },
        scales: {
          xAxes: [
            {
              beginAtZero: true,
              gridLines: {
                color: "#f1f5f9",
                drawBorder: false,
              },
              ticks: {
                display: true,
                fontFamily: "Open Sans",
                fontSize: 12,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                display: false,
                drawBorder: false,
              },
              ticks: {
                display: false,
              },
            },
          ],
        },
      };

      produtosChartInstances.current[chartKey] = new Chart(ctx, {
        type: "horizontalBar",
        data: chartData,
        options: options,
      });
    });
  };

  if (loading) {
    return (
      <Grid container spacing={2} style={{ margin: "1rem 0 0" }}>
        <Grid item xs={12}>
          <LoadingData searching label="Buscando dados de faturamento..." />
        </Grid>
      </Grid>
    );
  }

  const totalPedidos = parsePriceString(data?.TotalPedidos) || 0;
  const totalFaturado = parsePriceString(data?.TotalFaturado) || 0;
  const totalEmAberto = parsePriceString(data?.TotalEmAberto) || 0;

  return (
    <Grid container spacing={2} style={{ margin: "1rem 0 0" }}>
      <Grid item xs={12} md={4}>
        <BigNumberCard
          title="Total de pedidos"
          value={`R$ ${MaskAndValid.maskPrice(totalPedidos)}`}
          loading={loading}
          icon={<ListAltIcon fontSize="large" style={{ color: "#156082" }} />}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <BigNumberCard
          title="Faturado"
          value={`R$ ${MaskAndValid.maskPrice(totalFaturado)}`}
          loading={loading}
          icon={<ReceiptIcon fontSize="large" style={{ color: "#4CAF50" }} />}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <BigNumberCard
          title="Em aberto"
          value={`R$ ${MaskAndValid.maskPrice(totalEmAberto)}`}
          loading={loading}
          icon={
            <AttachMoneyIcon fontSize="large" style={{ color: "#9E9E9E" }} />
          }
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h4">Marca</Typography>
            <Box position="relative" height="300px">
              <canvas ref={marcaChartRef}></canvas>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h4">Linha de produto</Typography>
            <Box position="relative" height="300px">
              <canvas ref={linhaProdutoChartRef}></canvas>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {data?.RankingProdutos && data?.RankingProdutos?.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h4">Produtos mais comprados</Typography>
              <Grid container spacing={3}>
                {(() => {
                  const linhasAgrupadas = data.RankingProdutos.reduce(
                    (acc, produto) => {
                      const linha = produto.LinhaProduto || "Outros";
                      if (!acc[linha]) {
                        acc[linha] = [];
                      }
                      acc[linha].push(produto);
                      return acc;
                    },
                    {}
                  );

                  return Object.entries(linhasAgrupadas).map(([linha]) => {
                    const chartKey = linha;

                    return (
                      <Grid item xs={12} sm={6} md={3} key={linha}>
                        <Typography variant="h3" color="primary">
                          {linha}
                        </Typography>
                        <Box
                          position="relative"
                          style={{
                            height: 300,
                            marginTop: "1rem",
                          }}
                        >
                          <canvas
                            ref={(el) => {
                              produtosChartRefs.current[chartKey] = el;
                              if (el && data) {
                                setTimeout(() => {
                                  renderProdutosCharts();
                                }, 0);
                              }
                            }}
                          ></canvas>
                        </Box>
                      </Grid>
                    );
                  });
                })()}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
}
